import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Slide,
    FormControl,
    FormLabel,
    Switch,
    FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const CreateEditPackageModal = ({
    open,
    handleClose,
    handleSave,
    packageData = null,
    isLoading = false,
}) => {
    const isEditMode = !!packageData;

    const [formData, setFormData] = useState({
        packageName: '',
        credits: '',
        price: '',
        status: true,
    });

    const [errors, setErrors] = useState({});

    // Reset form when modal opens/closes or packageData changes
    useEffect(() => {
        if (open) {
            if (packageData) {
                setFormData({
                    packageName: packageData.packageName || '',
                    credits: packageData.credits?.toString() || '',
                    price: packageData.price?.toString() || '',
                    status: packageData.status === 'Active',
                });
            } else {
                setFormData({
                    packageName: '',
                    credits: '',
                    price: '',
                    status: true,
                });
            }
            setErrors({});
        }
    }, [open, packageData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.packageName.trim()) {
            newErrors.packageName = 'Package name is required';
        }

        if (!formData.credits) {
            newErrors.credits = 'Credits are required';
        } else if (isNaN(formData.credits) || parseInt(formData.credits) <= 0) {
            newErrors.credits = 'Credits must be a positive number';
        }

        if (!formData.price) {
            newErrors.price = 'Price is required';
        } else if (isNaN(formData.price) || parseFloat(formData.price) <= 0) {
            newErrors.price = 'Price must be a positive number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            handleSave({
                ...formData,
                credits: parseInt(formData.credits),
                price: parseFloat(formData.price),
                status: formData.status ? 'Active' : 'Inactive',
                ...(packageData && { id: packageData.id }),
            });
        }
    };

    const onClose = () => {
        if (!isLoading) {
            handleClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Gradient Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            {isEditMode ? 'Edit SMS Credit Package' : 'Create SMS Credit Package'}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            size="small"
                            disabled={isLoading}
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Box sx={{ px: 2.5, pb: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                bgcolor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            }}
                        >
                            <Icon
                                icon={isEditMode ? "mdi:pencil-box" : "mdi:package-variant-plus"}
                                width={32}
                                style={{ color: '#487fff' }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'white', mb: 0.5 }}>
                                {isEditMode ? 'Update package details' : 'Add a new SMS credit package'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {isEditMode ? 'Modify the package information below' : 'Fill in the details to create a new package'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Form Content */}
            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ p: 2.5 }}>
                    {/* Package Name */}
                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <FormLabel
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.875rem',
                            }}
                        >
                            Package Name <span style={{ color: '#ef4444' }}>*</span>
                        </FormLabel>
                        <input
                            type="text"
                            name="packageName"
                            value={formData.packageName}
                            onChange={handleChange}
                            placeholder="Enter package name"
                            className={`form-control ${errors.packageName ? 'is-invalid' : ''}`}
                            disabled={isLoading}
                        />
                        {errors.packageName && (
                            <div className="invalid-feedback">{errors.packageName}</div>
                        )}
                    </FormControl>

                    {/* Credits */}
                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <FormLabel
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.875rem',
                            }}
                        >
                            Credits <span style={{ color: '#ef4444' }}>*</span>
                        </FormLabel>
                        <input
                            type="number"
                            name="credits"
                            value={formData.credits}
                            onChange={handleChange}
                            placeholder="Enter number of credits"
                            className={`form-control ${errors.credits ? 'is-invalid' : ''}`}
                            disabled={isLoading}
                            min="1"
                        />
                        {errors.credits && (
                            <div className="invalid-feedback">{errors.credits}</div>
                        )}
                    </FormControl>

                    {/* Price */}
                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <FormLabel
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.875rem',
                            }}
                        >
                            Price (₹) <span style={{ color: '#ef4444' }}>*</span>
                        </FormLabel>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            placeholder="Enter price"
                            className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                            disabled={isLoading}
                            min="1"
                            step="0.01"
                        />
                        {errors.price && (
                            <div className="invalid-feedback">{errors.price}</div>
                        )}
                    </FormControl>

                    {/* Status Toggle */}
                    <FormControl fullWidth>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'white',
                            }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                    Package Status
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {formData.status ? 'Package is active and available' : 'Package is inactive and hidden'}
                                </Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="status"
                                        checked={formData.status}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                        color="success"
                                    />
                                }
                                label={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: formData.status ? 'success.main' : 'text.secondary',
                                        }}
                                    >
                                        {formData.status ? 'Active' : 'Inactive'}
                                    </Typography>
                                }
                                labelPlacement="start"
                                sx={{ m: 0 }}
                            />
                        </Box>
                    </FormControl>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1.5, bgcolor: '#f8fafc' }}>
                <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-20 py-10 text-sm fw-medium"
                    onClick={onClose}
                    disabled={isLoading}
                    style={{ flex: 1 }}
                >
                    <Icon icon="solar:close-circle-outline" className="me-6" />
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary radius-8 px-20 py-10 text-sm fw-medium"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{ flex: 1, backgroundColor: '#487fff', borderColor: '#487fff' }}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-8" role="status" aria-hidden="true"></span>
                            {isEditMode ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        <>
                            <Icon icon={isEditMode ? "mdi:content-save" : "mdi:plus-circle"} className="me-6" />
                            {isEditMode ? 'Update Package' : 'Create Package'}
                        </>
                    )}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateEditPackageModal;
